<template>
  <v-container fluid class="survey-page">
    <AError :api="apiGetSurvey" />

    <!-- Alert -->
    <AAlertBanner :messages="alertError" hide-title />

    <!-- Header -->
    <div class="survey-header">
      <div class="survey-header__top">
        <div v-if="!loadingDialog" class="survey-header__info">
          <h1 class="survey-header__title">{{ model.name.plural }}</h1>
          <span class="survey-header__count" v-if="surveyDatas.length > 0">{{ surveyDatas.length }} surveys</span>
        </div>
        <button class="survey-create-btn" @click="goCreate">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" width="16" height="16">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Create Survey
        </button>
      </div>
    </div>

    <!-- Survey Table -->
    <div class="survey-table-wrap">
      <v-data-table
        :headers="headers"
        :items="surveyDatas"
        :items-per-page="itemsPerPage"
        :page.sync="page"
        :loading="apiGetSurvey.isLoading"
        loading-text="Loading surveys..."
        no-data-text="No surveys found"
        item-key="id"
        class="survey-table"
        hide-default-footer
        @page-count="pageCount = $event"
      >
        <template v-slot:item.title="{ item }">
          <span class="survey-table__title">{{ item.title }}</span>
        </template>

        <template v-slot:item.status="{ item }">
          <span
            class="survey-status"
            :class="item.status ? 'survey-status--on' : 'survey-status--off'"
          >{{ item.status ? "Active" : "Inactive" }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left min-width="180">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" title="Actions">
                <v-icon size="20">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense class="survey-action-menu">
              <v-list-item @click="listAction('View Analytics', item)">
                <v-list-item-icon class="survey-action-menu__icon">
                  <v-icon size="18">mdi-chart-bar</v-icon>
                </v-list-item-icon>
                <v-list-item-title>View Analytics</v-list-item-title>
              </v-list-item>
              <v-list-item @click="goEdit(item)">
                <v-list-item-icon class="survey-action-menu__icon">
                  <v-icon size="18">mdi-pencil-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>
              <v-list-item @click="toggleStatus(item)">
                <v-list-item-icon class="survey-action-menu__icon">
                  <v-icon size="18">{{ item.status ? "mdi-eye-off-outline" : "mdi-eye-outline" }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.status ? "Deactivate" : "Activate" }}</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item @click="confirmDelete(item)">
                <v-list-item-icon class="survey-action-menu__icon">
                  <v-icon size="18" color="#ef4444">mdi-trash-can-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="red--text">Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>

      <!-- Pagination (taxPOD-styled) -->
      <div class="survey-pager" v-if="!apiGetSurvey.isLoading && surveyDatas.length > 0">
        <span class="survey-pager__counter">
          showing {{ page }} - {{ pageCount }} pages, {{ surveyDatas.length }} results found
        </span>
        <div class="survey-pager__controls">
          <span class="survey-pager__label">Page Size</span>
          <select class="survey-pager__size" v-model.number="itemsPerPage" @change="page = 1">
            <option v-for="opt in pageSizeOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
          <button class="survey-pager__btn" :class="{ disabled: page <= 1 }" :disabled="page <= 1" @click="page = 1">First</button>
          <button class="survey-pager__btn" :class="{ disabled: page <= 1 }" :disabled="page <= 1" @click="page > 1 && page--">Prev</button>
          <button
            v-for="p in visiblePages"
            :key="p"
            class="survey-pager__btn"
            :class="{ active: p === page }"
            @click="page = p"
          >{{ p }}</button>
          <button class="survey-pager__btn" :class="{ disabled: page >= pageCount }" :disabled="page >= pageCount" @click="page < pageCount && page++">Next</button>
          <button class="survey-pager__btn" :class="{ disabled: page >= pageCount }" :disabled="page >= pageCount" @click="page = pageCount">Last</button>
        </div>
      </div>
    </div>

    <!-- Delete confirmation (matches Delete Application design) -->
    <v-dialog v-model="deleteDialog" max-width="420" content-class="confirm-dialog">
      <div class="confirm-card">
        <h3 class="confirm-card__title">Delete Survey</h3>
        <p class="confirm-card__message">
          Are you sure to delete "{{ deleteTarget.title }}"?
        </p>
        <p class="confirm-card__hint">*Existing responses are kept for analytics</p>
        <div class="confirm-card__actions">
          <button class="confirm-card__btn confirm-card__btn--cancel" @click="deleteDialog = false">
            Cancel
          </button>
          <button class="confirm-card__btn confirm-card__btn--danger" @click="doDelete">
            Delete
          </button>
        </div>
      </div>
    </v-dialog>

    <SurveyResult
      :surveyResultModal="surveyResultModal"
      :surveyResultData="surveyResultData"
      @toggleSurveyResultModal="toggleSurveyResultModal"
    ></SurveyResult>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import moment from "moment";
import Api from "@/objects/api";
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";
import HrdcData from "@/services/hrdc/objects/globalData";
import SurveyResult from "./SurveyResult";

export default {
  components: { SurveyResult },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    // Show up to 3 numbered page buttons centered on the current page,
    // matching the taxPOD table's paginationButtonCount.
    visiblePages() {
      const total = this.pageCount;
      const maxBtns = 3;
      if (total <= maxBtns) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      let end = Math.min(total, Math.max(1, this.page - 1) + maxBtns - 1);
      const start = Math.max(1, end - maxBtns + 1);
      end = Math.min(total, start + maxBtns - 1);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  props: ["params"],
  data: () => ({
    apiGetSurvey: new Api(),
    apiGetSurveyAnalytic: new Api(),
    apiToggleStatus: new Api(),
    apiDelete: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    errorField: {
      applicationNo: null,
    },
    loadingDialog: false,
    headers: [
      {
        text: "No.",
        align: "start",
        sortable: false,
        value: "index",
      },
      { text: "Title", value: "title", sortable: false },
      { text: "Status", value: "status", sortable: false, align: "center" },
      { text: "Active Period", value: "period", sortable: false },
      { text: "Created Date", value: "timestampCreated", sortable: false },
      { text: "Actions", value: "actions", sortable: false, align: "center" },
    ],
    surveyDatas: [],
    page: 1,
    pageCount: 0,
    itemsPerPage: 10,
    pageSizeOptions: [10, 25, 50, 100],
    surveyResultModal: false,
    surveyResultData: {},
    surveyResult: [],
    deleteDialog: false,
    deleteTarget: {},
  }),
  async created() {
    this.showLoadingDialog();
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiGetSurvey: {
        url: `${this.$service[this.service.key]}/v1/en/console/survey`,
      },
      apiGetSurveyAnalytic: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/survey/get-survey-analytic-by-uuid`,
      },
      apiToggleStatus: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/survey/toggle-status`,
        method: "post",
      },
      apiDelete: {
        url: `${this.$service[this.service.key]}/v1/en/console/survey/delete`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].query !== undefined) {
        this[api].setQuery(apiDetails[api].query);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    this.rolesAssignation(this.auth.roles);

    this.apiGetSurvey.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;

        this.surveyDatas = [];

        if (!status) {
          this.$store.dispatch("showMessage", {
            message: data,
            messageType: "error",
            timeout: 2000,
          });

          return;
        }

        if (status) {
          if (data) {
            let index = 1;
            for (const item of data) {
              const {
                id,
                uuid,
                title,
                status: surveyStatus,
                timestampStart,
                timestampEnd,
                timestampCreated,
              } = item;

              this.surveyDatas.push({
                id,
                uuid,
                index,
                title,
                status: surveyStatus,
                period: this.formatPeriod(timestampStart, timestampEnd),
                timestampCreated: moment(timestampCreated).format(
                  "YYYY-MM-DD HH:mm:ss"
                ),
                action: ["View Analytics"],
              });
              index++;
            }
          }
        }
        this.hideLoadingDialog();
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetSurvey.fetch();

    this.hideLoadingDialog();
  },
  methods: {
    redirectBack() {
      let route = "HrdcSettings";
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
      });
    },
    listAction(action, item) {
      switch (action) {
        case "View Analytics":
          // Get survey data by uuid.
          this.showLoadingDialog();
          this.apiGetSurveyAnalytic.setQuery(`surveyUuid=${item.uuid}`);
          this.apiGetSurveyAnalytic.setCallbackCompleted((response) => {
            try {
              const { status, data } = response;
              if (status) {
                let chartData = {};
                let chartOptions = {};
                const { surveyQuestion, surveyResult, groupSurveyResponses } =
                  data;

                this.surveyResult = surveyResult;
                for (const question of surveyQuestion) {
                  // Store question to chartOptions.
                  const chartOpts = this.getChartOptions(question);
                  chartOptions[question.uuid] = chartOpts;

                  const chartItem = this.getChartData(question);
                  chartData[question.uuid] = chartItem;
                }

                this.$set(this.surveyResultData, "survey", item);
                this.$set(
                  this.surveyResultData,
                  "groupSurveyResponses",
                  groupSurveyResponses
                );
                this.$set(
                  this.surveyResultData,
                  "surveyQuestion",
                  surveyQuestion
                );
                this.$set(this.surveyResultData, "chartOptions", chartOptions);
                this.$set(this.surveyResultData, "chartData", chartData);

                // Open result modal.
                this.surveyResultModal = true;
              }
            } catch (err) {
              console.log(err);
            } finally {
              this.hideLoadingDialog();
            }
          });
          this.apiGetSurveyAnalytic.fetch();
          break;
      }
    },
    toggleSurveyResultModal(val) {
      this.surveyResultModal = val;
    },
    formatPeriod(start, end) {
      const fmt = (d) => moment(d).format("YYYY-MM-DD");
      if (start && end) return `${fmt(start)} → ${fmt(end)}`;
      if (start) return `From ${fmt(start)}`;
      if (end) return `Until ${fmt(end)}`;
      return "No date limit";
    },
    goCreate() {
      this.$router.push({
        name: "HrdcSettingsSurveyAdd",
        params: { modelKey: this.model.key },
      });
    },
    goEdit(item) {
      this.$router.push({
        name: "HrdcSettingsSurveyEdit",
        params: { modelKey: this.model.key, surveyUuid: item.uuid },
      });
    },
    toggleStatus(item) {
      this.showLoadingDialog();
      this.apiToggleStatus.setParams({
        surveyUuid: item.uuid,
        status: !item.status,
      });
      this.apiToggleStatus.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          this.$store.dispatch("showMessage", {
            message: typeof data === "string" ? data : "Status updated.",
            messageType: status ? "success" : "error",
            timeout: 2000,
          });
          if (status) this.apiGetSurvey.fetch();
        } catch (err) {
          console.log(err);
        } finally {
          this.hideLoadingDialog();
        }
      });
      this.apiToggleStatus.fetch();
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    doDelete() {
      this.deleteDialog = false;
      this.showLoadingDialog();
      this.apiDelete.setParams({ surveyUuid: this.deleteTarget.uuid });
      this.apiDelete.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          this.$store.dispatch("showMessage", {
            message: typeof data === "string" ? data : "Survey deleted.",
            messageType: status ? "success" : "error",
            timeout: 2000,
          });
          if (status) this.apiGetSurvey.fetch();
        } catch (err) {
          console.log(err);
        } finally {
          this.hideLoadingDialog();
        }
      });
      this.apiDelete.fetch();
    },
    getChartOptions(question) {
      const modernColors = [
        '#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981',
        '#ef4444', '#ec4899', '#6366f1', '#14b8a6',
        '#f97316', '#06b6d4',
      ];
      const fontName = 'Lato, Segoe UI, sans-serif';

      switch (question.answerType) {
        default:
        case "radio":
          return {
            chartType: "PieChart",
            questionTitle: question.question,
            pieSliceText: "none",
            pieHole: 0.52,
            colors: modernColors,
            backgroundColor: 'transparent',
            fontName,
            fontSize: 13,
            legend: {
              position: 'labeled',
              alignment: 'center',
              textStyle: { color: '#334155', fontSize: 12, fontName },
            },
            chartArea: {
              width: '90%',
              height: '80%',
              top: 8,
            },
            tooltip: {
              text: 'both',
              textStyle: { fontName, fontSize: 13 },
            },
            pieSliceBorderColor: '#fff',
          };

        case "checkbox":
          return {
            chartType: "BarChart",
            questionTitle: question.question,
            colors: modernColors,
            backgroundColor: 'transparent',
            fontName,
            fontSize: 12,
            chartArea: {
              width: '55%',
              height: '80%',
              top: 16,
            },
            hAxis: {
              minValue: 0,
              textStyle: { color: '#94a3b8', fontSize: 11, fontName },
              gridlines: { color: '#f1f5f9', count: 5 },
              baselineColor: '#e2e8f0',
            },
            vAxis: {
              textStyle: { color: '#334155', fontSize: 12, fontName },
            },
            legend: { position: 'none' },
            bar: { groupWidth: '60%' },
            tooltip: {
              textStyle: { fontName, fontSize: 13 },
            },
          };
      }
    },
    getChartData(question) {
      const formattedData = [];
      const answerMap = {};
      const { answerType, SurveyOption } = question;
      const results = this.surveyResult.filter(
        (result) => result.surveyQuestionUuid == question.uuid
      );
      let latestResponses = "";
      let textareaResponses;

      SurveyOption.forEach((option) => {
        answerMap[option.option] = 0;
      });

      switch (answerType) {
        default:
        case "radio":
          for (const result of results) {
            const parsedAns = JSON.parse(result.answer);
            if (Object.prototype.hasOwnProperty.call(answerMap, parsedAns)) {
              answerMap[parsedAns] += result._count._all;
            }
          }

          for (const [key, value] of Object.entries(answerMap)) {
            formattedData.push([key, value]);
          }

          return [["Question", "Responses"], ...formattedData];

        case "checkbox": {
          const barColors = [
            '#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981',
            '#ef4444', '#ec4899', '#6366f1', '#14b8a6',
            '#f97316', '#06b6d4',
          ];

          for (const result of results) {
            const parsedAnsArr = JSON.parse(result.answer);

            for (const parseAns of parsedAnsArr) {
              if (Object.prototype.hasOwnProperty.call(answerMap, parseAns)) {
                answerMap[parseAns] += result._count._all;
              }
            }
          }

          let colorIdx = 0;
          for (const [key, value] of Object.entries(answerMap)) {
            formattedData.push([
              key,
              value,
              barColors[colorIdx % barColors.length],
            ]);
            colorIdx++;
          }

          return [
            ["Question", "Responses", { role: "style" }],
            ...formattedData,
          ];
        }

        case "textarea":
          textareaResponses = results;

          for (const result of results.slice(0, 2)) {
            latestResponses += `${JSON.parse(result.answer)}<br>`;
          }

          return {
            latestResponses,
            textareaResponses,
          };
      }
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .survey-page {
  background: var(--app-page);
}

.dark-mode .survey-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-card);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .survey-header__count {
  background: var(--app-accent-soft);
}

.dark-mode .survey-table-wrap {
  background: var(--app-card);
}

.dark-mode .survey-table >>> thead th {
  color: var(--app-text-muted) !important;
  background: var(--app-surface);
}

.dark-mode .survey-table >>> tbody td {
  color: var(--app-text);
}

.dark-mode .survey-table >>> tbody tr:hover {
  background: var(--app-surface) !important;
}

.dark-mode .survey-table__title {
  color: var(--app-text);
}

.dark-mode .survey-pager {
  background: var(--app-surface);
  border-top: 2px solid var(--app-border);
}

.dark-mode .survey-pager__counter {
  color: var(--app-text-faint);
}

.dark-mode .survey-pager__label {
  color: var(--app-text-muted);
}

.dark-mode .survey-pager__size {
  border: 1.5px solid var(--app-border-strong);
  color: var(--app-text);
  background: var(--app-card);
}

.dark-mode .survey-pager__btn {
  color: var(--app-text-muted);
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .survey-pager__btn:hover:not(.disabled):not(.active) {
  background: var(--app-accent-soft);
}

.dark-mode .survey-status--on {
  background: var(--app-success-bg);
}

.dark-mode .survey-status--off {
  background: var(--app-elevated);
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

/* ===== PAGE ===== */
.survey-page {
  background: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: hidden;
  zoom: 1.1;
}

/* ===== HEADER ===== */
.survey-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.survey-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px 16px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.survey-header__info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.survey-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  letter-spacing: -0.2px;
}

.survey-header__count {
  font-size: 0.72rem;
  font-weight: 600;
  color: #0ea5e9;
  background: #e0f2fe;
  padding: 3px 10px;
  border-radius: 20px;
}

/* ===== ROW ACTIONS MENU ===== */
.survey-action-menu {
  padding: 4px 0 !important;
}
.survey-action-menu >>> .v-list-item {
  min-height: 32px !important;
  padding: 0 12px !important;
}
.survey-action-menu >>> .v-list-item__title {
  font-size: 0.8rem !important;
  line-height: 1.2 !important;
}
.survey-action-menu >>> .v-divider {
  margin: 4px 0 !important;
}
.survey-action-menu__icon {
  margin: 0 8px 0 0 !important;
  min-width: 0 !important;
  align-self: center !important;
}

/* ===== CREATE BUTTON ===== */
.survey-create-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 9px 16px;
  border-radius: 10px;
  border: none;
  background: #0ea5e9;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: "Lato", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}
.survey-create-btn:hover {
  background: #0284c7;
}

/* ===== TABLE ===== */
.survey-table-wrap {
  background: #fff;
  border: 1px solid #e4e8ec;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
}
.survey-table {
  background: transparent !important;
}
.survey-table >>> thead th {
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b !important;
  background: #f8fafc;
  white-space: nowrap;
}
.survey-table >>> tbody td {
  font-size: 0.85rem;
  color: #334155;
}
.survey-table >>> tbody tr:hover {
  background: #f8fafc !important;
}
.survey-table__title {
  font-weight: 700;
  color: #1e293b;
}

/* ===== PAGINATION (matches taxPOD applications table) ===== */
.survey-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  background: #f8fafc;
  border-top: 2px solid #e2e8f0;
  padding: 10px 16px;
  font-family: "Lato", sans-serif;
}
.survey-pager__counter {
  font-size: 0.75rem;
  color: #94a3b8;
}
.survey-pager__controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.survey-pager__label {
  font-size: 0.78rem;
  color: #64748b;
  margin-right: 4px;
}
.survey-pager__size {
  font-family: "Lato", sans-serif;
  font-size: 0.78rem;
  border: 1.5px solid #d1d5db;
  border-radius: 6px;
  padding: 4px 8px;
  color: #334155;
  background: #fff;
  cursor: pointer;
  outline: none;
  margin-right: 8px;
}
.survey-pager__btn {
  font-family: "Lato", sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px 10px;
  margin: 0 2px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
}
.survey-pager__btn:hover:not(.disabled):not(.active) {
  background: #f0f9ff;
  border-color: #0ea5e9;
  color: #0ea5e9;
}
.survey-pager__btn.active {
  background: #0ea5e9;
  border-color: #0ea5e9;
  color: #fff;
  cursor: default;
}
.survey-pager__btn.disabled,
.survey-pager__btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* ===== STATUS PILL ===== */
.survey-status {
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 20px;
  flex-shrink: 0;
}
.survey-status--on {
  background: #dcfce7;
  color: #16a34a;
}
.survey-status--off {
  background: #f1f5f9;
  color: #94a3b8;
}

/* Delete dialog reuses the global .confirm-card styles from TheModalConfirmation. */

/* ===== DARK MODE ===== */
.dark-mode .survey-page {
  background: var(--app-page);
}

.dark-mode .survey-header {
  background: var(--app-page);
}

.dark-mode .survey-header__title {
  color: var(--app-text);
}

.dark-mode .survey-header__count {
  background: var(--app-accent-soft);
  color: var(--app-accent);
}

.dark-mode .back-btn {
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .back-btn:hover {
  background: var(--app-card);
  border-color: var(--app-border-strong);
}

.dark-mode .survey-table-wrap {
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .survey-table >>> thead th {
  background: var(--app-page);
  color: var(--app-text-muted) !important;
}

.dark-mode .survey-table >>> tbody td {
  color: var(--app-neutral-fg);
}

.dark-mode .survey-table >>> tbody tr:hover {
  background: var(--app-card) !important;
}

.dark-mode .survey-table__title {
  color: var(--app-text);
}

.dark-mode .survey-pager {
  background: var(--app-page);
  border-top-color: var(--app-border);
}
.dark-mode .survey-pager__size,
.dark-mode .survey-pager__btn {
  background: var(--app-page);
  border-color: var(--app-border);
  color: var(--app-neutral-fg);
}
.dark-mode .survey-pager__btn:hover:not(.disabled):not(.active) {
  background: var(--app-accent-soft);
  border-color: var(--app-accent);
  color: var(--app-accent-strong);
}
.dark-mode .survey-pager__btn.active {
  background: var(--app-accent);
  border-color: var(--app-accent);
  color: var(--app-accent-fg);
}

.dark-mode .survey-action-menu {
  background: var(--app-page);
}

/* ===== MOBILE ===== */
@media (max-width: 599px) {
  .survey-page {
    padding: 12px 8px !important;
    zoom: 1;
  }

  .survey-header {
    border-radius: 14px;
    margin-bottom: 16px;
  }

  .survey-header__top {
    padding: 16px 16px 12px;
  }

  .survey-header__title {
    font-size: 1.05rem;
  }

  .survey-table >>> thead th,
  .survey-table >>> tbody td {
    padding: 0 10px !important;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .survey-page {
    zoom: 1;
  }
}
</style>
